/**
 * UTM Tracking Utility
 * 
 * Captures and stores UTM parameters from URL query strings.
 * Parameters are stored in sessionStorage to persist across page navigations.
 */

export interface UtmParams {
  utm_source: string;
  utm_medium: string;
  utm_term: string;
  utm_campaign: string;
}

const STORAGE_KEY = 'octet_utm_params';
const UTM_PARAM_KEYS = ['utm_source', 'utm_medium', 'utm_term', 'utm_campaign'] as const;

/**
 * Extract UTM parameters from a URL or URLSearchParams
 */
export function extractUtmParams(searchParams: URLSearchParams | string): Partial<UtmParams> {
  const params: Partial<UtmParams> = {};
  
  // Handle both URLSearchParams and string inputs
  const urlParams = typeof searchParams === 'string' 
    ? new URLSearchParams(searchParams) 
    : searchParams;
  
  UTM_PARAM_KEYS.forEach(key => {
    const value = urlParams.get(key);
    if (value) {
      params[key] = value;
    }
  });
  
  return params;
}

/**
 * Get UTM parameters from sessionStorage
 */
export function getStoredUtmParams(): Partial<UtmParams> {
  if (typeof window === 'undefined') {
    return {};
  }
  
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to read UTM parameters from sessionStorage:', error);
  }
  
  return {};
}

/**
 * Store UTM parameters in sessionStorage
 * Merges with existing parameters (new values override old ones)
 */
export function storeUtmParams(params: Partial<UtmParams>): void {
  if (typeof window === 'undefined') {
    return;
  }
  
  try {
    const existing = getStoredUtmParams();
    const merged = { ...existing, ...params };
    
    // Only store if there are actual values
    const hasValues = Object.values(merged).some(value => value && value.trim() !== '');
    if (hasValues) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    }
  } catch (error) {
    console.warn('Failed to store UTM parameters in sessionStorage:', error);
  }
}

/**
 * Clear stored UTM parameters
 */
export function clearUtmParams(): void {
  if (typeof window === 'undefined') {
    return;
  }
  
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear UTM parameters from sessionStorage:', error);
  }
}

/**
 * Get UTM parameters formatted for Dynamics API
 * Returns empty strings for missing parameters
 */
export function getUtmParamsForDynamics(): {
  ot_utmsource: string;
  ot_utmmedium: string;
  ot_utmterm: string;
  ot_utmcampaign: string;
} {
  const params = getStoredUtmParams();
  
  return {
    ot_utmsource: params.utm_source || '',
    ot_utmmedium: params.utm_medium || '',
    ot_utmterm: params.utm_term || '',
    ot_utmcampaign: params.utm_campaign || '',
  };
}

